import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button, CardBox, Link, NotificationDialog, Stack, Text } from '../../components'
import { mutations, queries } from '../../graphql'

const CONTEXT_COPY = {
  advisor: {
    body: 'This will immediately delete your Advisor Portal access and all associated advisor data.',
    button: 'Delete Advisor Portal Access',
    confirmTitle: 'Delete Advisor Portal Access',
    confirmMessage:
      'Are you sure you want to delete your Advisor Portal access? This will immediately remove all associated advisor data.',
    successTitle: 'Advisor Portal Deleted',
    successMessage: 'Your Advisor Portal access has been deleted immediately.',
  },
  charity: {
    body: 'This will immediately delete your Charity Portal access and all associated representative data.',
    button: 'Delete Charity Portal Access',
    confirmTitle: 'Delete Charity Portal Access',
    confirmMessage:
      'Are you sure you want to delete your Charity Portal access? This will immediately remove all associated representative data.',
    successTitle: 'Charity Portal Deleted',
    successMessage: 'Your Charity Portal access has been deleted immediately.',
  },
}

export function DeletePortalAccessTab({ context }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [successOpen, setSuccessOpen] = useState(false)
  const copy = CONTEXT_COPY[context]
  const { id: charityId } = useParams()

  const { data: { me } = {} } = useQuery(queries.user.me, { fetchPolicy: 'cache-first' })

  const fundProfileId = (() => {
    const profiles = me?.fundProfiles || []
    if (context === 'charity') {
      const charityFund = (me?.funds || []).find((f) => f.name === `charity-${charityId}`)
      return profiles.find((p) => p.fundId != null && p.fundId === charityFund?.id)?.id
    }
    return profiles.find((p) => p.fundType === context && !p.closed)?.id
  })()

  const [closePortal, { loading }] = useMutation(mutations.user.closeMyPortal)

  const handleConfirm = async () => {
    try {
      await closePortal({ variables: { fundProfileId } })
      setConfirmOpen(false)
      setSuccessOpen(true)
    } catch (err) {
      console.error('Error closing portal access:', err)
      setConfirmOpen(false)
    }
  }

  const handleSuccess = () => {
    setSuccessOpen(false)
    window.location.href = '/logout'
  }

  return (
    <Stack>
      <CardBox>
        <Text.H4>Delete Portal Access</Text.H4>
        <br />
        <Stack>
          <Text.Body>{copy.body}</Text.Body>
          <Text.Body>If you&apos;re sure, please confirm by clicking the button below.</Text.Body>
          <Button onClick={() => setConfirmOpen(true)} color="error" disabled={!fundProfileId}>
            {copy.button}
          </Button>
          <br />
          <Text.Body>
            Need help? Contact us at{' '}
            <Link href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`}>{process.env.REACT_APP_SUPPORT_EMAIL}</Link>{' '}
            for assistance.
          </Text.Body>
        </Stack>
        <br />

        <NotificationDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          type="alert"
          title={copy.confirmTitle}
          message={
            <>
              {copy.confirmMessage}
              <br />
              <br />
              <Text.H7>
                If you have any questions, please contact us at {process.env.REACT_APP_SUPPORT_EMAIL} before proceeding.
              </Text.H7>
            </>
          }
          actions={[
            { label: 'Cancel', onClick: () => setConfirmOpen(false) },
            { label: loading ? 'Deleting...' : copy.button, onClick: handleConfirm, disabled: loading },
          ]}
        />

        <NotificationDialog
          open={successOpen}
          onClose={handleSuccess}
          type="success"
          title={copy.successTitle}
          message={copy.successMessage}
          actions={[{ label: 'OK', onClick: handleSuccess }]}
        />
      </CardBox>
    </Stack>
  )
}
